import { Typography } from '@material-ui/core';
import { IList, tuple } from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { viewTexts } from '../../../constants/view-texts';
import { IProjectImageInfoType } from '../../../types/record/image-info';
import { ImageThumbnailGridCell } from '../../shared/image-thumbnail/image-thumbnail-grid-cell';
import { MuiSwitch } from '../../shared/mui/mui-switch';
import { H2 } from '../../shared/styled/heading';
import { Root } from '../../shared/styled/root';
import { DndFileUploadArea } from './dnd-file-upload-area/dnd-file-upload-area';

const vt = viewTexts.pages.projectImageList;

const DropzoneWrapper = styled.div`
  padding: 10px;
`;

const Switchs = styled.div`
  display: flex;
  align-items: center;
`;

const FilterSwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;
  margin-right: 20px;
`;

const BulkDeleteSwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;
`;

const ImagePreviewWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const AnnotatedRatio = styled.div`
  padding: 5px;
`;

interface Props {
  imageList: IList<IProjectImageInfoType>;
  imageListFiltered: IList<IProjectImageInfoType>;
  onImageClick: (imgId: number) => void;
  onDeleteClick: (imgId: number) => void;
  annotatedIsVisible: boolean;
  showAll: () => void;
  hideAnnotated: () => void;
  IsBulkDelete: boolean;
  InstantDelete: () => void;
  ConfirmDelete: () => void;
}

export const ProjectImageListView: FC<Props> = memo(
  ({
    imageList,
    imageListFiltered,
    onImageClick,
    onDeleteClick,
    annotatedIsVisible,
    showAll,
    hideAnnotated,
    IsBulkDelete,
    InstantDelete,
    ConfirmDelete
  }: Readonly<Props>) => {
    const imagesWithHandler = useMemo(
      () =>
        imageListFiltered.map(img =>
          tuple(
            img,
            () => onImageClick(img.id),
            () => onDeleteClick(img.id)
          )
        ),
      [imageListFiltered, onImageClick, onDeleteClick]
    );

    const filterSwitchHandler = useCallback(
      (checked: boolean) => {
        if (checked) {
          hideAnnotated();
        } else {
          showAll();
        }
      },
      [showAll, hideAnnotated]
    );

    const bulkDeleteSwitchHandler = useCallback(
      (checked: boolean) => {
        if (checked) {
          InstantDelete();
        } else {
          ConfirmDelete();
        }
      },
      [InstantDelete, ConfirmDelete]
    );

    const numAnnotated = useMemo<number>(
      () => imageList.filter(e => e.isAnnotated).size,
      [imageList]
    );

    return (
      <Root>
        <H2>{vt.title}</H2>
        <AnnotatedRatio>
          <Typography>{vt.numCounted(imageList.size, numAnnotated)}</Typography>
        </AnnotatedRatio>
        <DropzoneWrapper>
          <DndFileUploadArea />
        </DropzoneWrapper>
        <Switchs>
          <FilterSwitchWrapper>
            <span>{vt.showAll}</span>
            <MuiSwitch
              color='default'
              checked={!annotatedIsVisible}
              onChange={filterSwitchHandler}
            />
            <span>{vt.hideCounted}</span>
          </FilterSwitchWrapper>
          <BulkDeleteSwitchWrapper>
            <span>{vt.isBuklDelete}</span>
            <MuiSwitch
              color='secondary'
              checked={IsBulkDelete}
              onChange={bulkDeleteSwitchHandler}
            />
          </BulkDeleteSwitchWrapper>
        </Switchs>
        <ImagePreviewWrapper>
          {imagesWithHandler.map(
            ([img, onImageClickHandler, onDeleteClickHandler]) => (
              <ImageThumbnailGridCell
                key={img.id}
                image={img}
                chipLabel={vt.isCounted}
                buttonLabel={IsBulkDelete ? viewTexts.others.instantDelete : viewTexts.others.delete}
                onImageClick={onImageClickHandler}
                onDeleteClick={onDeleteClickHandler}
              />
            )
          )}
        </ImagePreviewWrapper>
      </Root>
    );
  }
);

ProjectImageListView.displayName = 'ProjectImageListView';
